import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Modal, Row, message, notification } from 'antd';
import CcxComponentProps from '../../../../core/CcxComponent';
import DeploymentsItem from '../../../../types/DeploymentsItem';
import useContentDeployWizard from '../../../../core/hooks/useContentDeployWizard';
import ProvisionService from '../../../../services/ProvisionService';
import CcxIconInfoCircleTwoTone from '../../../ccx/icons/CcxIconInfoCircleTwoTone';
import CcxIconCloseCircleTwoTone from '../../../ccx/icons/CcxIconCloseCircleTwoTone';
import InfoIcon from '@severalnines/bar-frontend-components/build/lib/General/InfoIcon';
import PrettyNumber from '../../../ccx/common/PrettyNumber';
import DatastoreScalingInfoCard from '../DatastoreScalingInfoCard';
import DatastoreScalingStep2 from '../DatastoreScalingStep2';
import ServiceService from '../../../../services/ServiceService';
import { billingDisabled } from '../../../../core/CcxEnv';
import DeploymentOptions from '../../../../types/DeploymentOptions';

interface Props extends CcxComponentProps {
    datastore: DeploymentsItem;
    job: any[];
    services: any;
    refresh: () => void;
    clearStoreInterval: () => void;
    startStoreInterval: () => void;
    setIsButtonDisabled: Function;
    isButtonDisabled: Boolean;
}

function ScaleNodeModal({
    testId = 'ScaleNodeModal',
    datastore,
    job,
    services,
    refresh,
    clearStoreInterval,
    startStoreInterval,
    setIsButtonDisabled,
    isButtonDisabled,
}: Props) {
    const [visible, setVisible] = useState(false);
    const [form] = Form.useForm();
    const [storage, setStorage] = useState<number>(datastore?.volumeSize);

    const [nodeValue, setNodeValue] = useState<number | undefined>(
        services?.dbServices?.length * 20
    );
    useEffect(() => {
        setNodeValue(datastore && services?.dbServices?.length * 20);
    }, [datastore, services?.dbServices?.length]);

    const dataStoreUuid = datastore?.dataStoreUuid;
    const [monthlyCost, setMonthlyCost] = useState(0);
    const [countryCode, setCountryCode] = useState<string | undefined>(
        undefined
    );
    const [checkedValues, setCheckedValues] = useState<string[]>([]);
    const [newNodeData, setNewNodeData] = useState<any[]>([]);

    const { deploymentOptions } = useContentDeployWizard();

    const onClose = () => {
        clearStoreInterval();
        setVisible(false);
    };

    const showModal = () => {
        setVisible(true);
        clearStoreInterval();
    };

    useEffect(() => {
        if (nodeValue! / 20 >= services?.dbServices?.length) {
            setCheckedValues([]);
        } else {
            setNewNodeData([]);
        }
    }, [nodeValue, services?.dbServices?.length]);

    const handleSubmit = async () => {
        if (
            (checkedValues.length !== 0 &&
                checkedValues.length ===
                    Math.abs(
                        nodeValue! / 20 - services?.dbServices?.length!
                    )) ||
            newNodeData?.length !== 0
        ) {
            try {
                if (
                    checkedValues.length ===
                    Math.abs(nodeValue! / 20 - services?.dbServices?.length!)
                ) {
                    await ProvisionService.scaleDownDatastoreNodes({
                        dataStoreUuid: dataStoreUuid,
                        nodeUuids: checkedValues,
                    });
                } else if (newNodeData && newNodeData.length > 0) {
                    await ProvisionService.scaleUpDatastoreNodes({
                        dataStoreUuid: dataStoreUuid,
                        nodeData: newNodeData,
                    });
                }
                setNewNodeData([]);
                setCheckedValues([]);
                setIsButtonDisabled(true);
                await ServiceService.getAllJobs(dataStoreUuid);
                refresh && (await refresh());
                notification.open({
                    message: 'Datastore configuration',
                    description:
                        'Your datastore configuration will updated shortly.',
                    icon: <CcxIconInfoCircleTwoTone />,
                });

                onClose();
                startStoreInterval();
                setTimeout(() => {
                    setIsButtonDisabled(false);
                }, 5000);
            } catch (error) {
                notification.open({
                    message: 'Error',
                    description: `An error occurred while processing the requests. ${error}`,
                    icon: <CcxIconCloseCircleTwoTone twoToneColor="#eb2f96" />,
                });
            }
        } else {
            message.error('One or more required fields are missing');
        }
    };

    const getPriceByCode = (code: string) => {
        const deployOption = deploymentOptions?.getInstanceSizes(
            datastore?.cloudProvider
        );
        const option = deployOption?.find((option) => option.code === code);
        return option ? option.price : null;
    };

    const prices = useMemo(() => {
        if (newNodeData) {
            return newNodeData.map((node) => {
                const { instance_size } = node;
                const price = getPriceByCode(instance_size);
                return { ...node, price };
            });
        }
        return undefined;
    }, [newNodeData]);

    const sumOfInstancesPrice = useMemo(() => {
        if (newNodeData) {
            return prices?.reduce(
                (accumulator, current) => accumulator + (current.price || 0),
                0
            );
        }
        return undefined;
    }, [newNodeData, prices]);

    const numberOfNodes = services?.dbServices?.length + newNodeData?.length;

    const getVolumeTypePrice = (
        deploymentOptions: DeploymentOptions | undefined,
        datastore: DeploymentsItem
    ) => {
        const volumeTypes =
            deploymentOptions?.instance?.volumeTypes[
                datastore?.cloudProvider?.code
            ];

        const volumeTypePrice = volumeTypes?.find(
            (v: any) => v.code === datastore?.volumeType
        )?.price;

        return volumeTypePrice / 100;
    };

    useEffect(() => {
        let instanceMonthlyPrice = (sumOfInstancesPrice / 100) * 730 || 0;
        let instanceNumber = numberOfNodes || 0;
        let storageMonthlyPrice =
            getVolumeTypePrice(deploymentOptions, datastore) || 0;
        let numberOfGB = storage || 0;
        let iopsMonthlyPrice = 0;
        let numberOfIOPS = 0;

        let monthlyPriceEstimate =
            instanceMonthlyPrice * instanceNumber +
            storageMonthlyPrice * instanceNumber * numberOfGB +
            iopsMonthlyPrice * instanceNumber * numberOfIOPS;

        setMonthlyCost(parseFloat(monthlyPriceEstimate.toFixed(2)));
    }, [getVolumeTypePrice, numberOfNodes, storage, sumOfInstancesPrice]);

    return (
        <>
            <Button
                disabled={job?.length > 0 || !!isButtonDisabled}
                onClick={showModal}
            >
                Scale Nodes
            </Button>
            <Modal
                title={'Scale Nodes'}
                open={visible}
                data-testid={`${testId}Modal`}
                width={800}
                onCancel={onClose}
                footer={
                    <Row justify={billingDisabled ? 'end' : 'space-between'}>
                        {!billingDisabled && (
                            <Col>
                                <div className="">
                                    <div>
                                        Estimated total cost{' '}
                                        <InfoIcon
                                            info={
                                                <span>
                                                    This amount will be added to
                                                    your next invoice.
                                                </span>
                                            }
                                        />{' '}
                                    </div>
                                    <div
                                        style={{
                                            color: '#C41D7F',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                        }}
                                    >
                                        <PrettyNumber
                                            prefix={<>&#36;</>}
                                            value={monthlyCost}
                                            sufix={' / month'}
                                        />
                                    </div>
                                </div>
                            </Col>
                        )}
                        <Col>
                            <Button
                                data-testid={`${testId}CancelButton`}
                                onClick={onClose}
                            >
                                Cancel
                            </Button>

                            <Button
                                // className={`ant-btn-lg`}
                                data-testid={`${testId}SubmitButton`}
                                onClick={handleSubmit}
                                type="primary"
                            >
                                Save
                            </Button>
                        </Col>
                    </Row>
                }
            >
                <DatastoreScalingInfoCard
                    dataStore={datastore}
                    setCountryCode={setCountryCode}
                    countryCode={countryCode}
                />
                <DatastoreScalingStep2
                    dataStore={datastore}
                    services={services}
                    countryCode={countryCode}
                    form={form}
                    checkedValues={checkedValues}
                    setNewNodeData={setNewNodeData}
                    newNodeData={newNodeData}
                    setCheckedValues={setCheckedValues}
                    deploymentOptions={deploymentOptions!}
                    setNodeValue={setNodeValue}
                    nodeValue={nodeValue!}
                />
            </Modal>
        </>
    );
}

export default ScaleNodeModal;
